import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { localIsoDay } from "@/lib/dates";
import { PageHeader, StatusBadge, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MoreHorizontal, Plus, Truck, Trash2, AlertCircle, Wrench, Camera, ChevronDown, Edit3, User, X, BarChart3, ChevronLeft, ChevronRight, Activity, Upload, FileText, Calendar, ExternalLink, Download, Search, LayoutList, LayoutGrid, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { PhotoGrid, AuthImage } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { expiryStatus, isExpiringSoon } from "@/lib/expiry";
import { meterTypeFor, meterInfo } from "@/lib/meterType";

export const fleetProfilePath = id => `/app/plant/fleet/${encodeURIComponent(id)}`;
// Portal menus bubble through React parents too; only bare row/card areas navigate.
export const isFleetAction = event => !!event.target.closest('a, button, input, select, textarea, [role="combobox"], [role="menuitem"], [role="option"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper], [data-fleet-action]');

const isPastOrSoon = (d) => isExpiringSoon(d);

// Option list is also used to normalise legacy stored values (e.g. "roller")
// so the Type select never renders blank when editing an older record.
const VEHICLE_TYPE_OPTIONS = [
  ["Ute", "Ute"], ["Truck", "Truck"], ["Van", "Van"], ["Car", "Car"],
  ["Digger", "Digger / Excavator"], ["Loader", "Loader"], ["Bulldozer", "Bulldozer / Dozer"],
  ["Roller", "Roller / Compactor"], ["Grader", "Grader"], ["Skid Steer", "Skid Steer / Bobcat"],
  ["Dumper", "Dumper"], ["Tractor", "Tractor"], ["Crane", "Crane"], ["Telehandler", "Telehandler"],
  ["Trailer", "Trailer"], ["Fuel Bowser Trailer", "Fuel bowser trailer"], ["Attachment", "Attachment"], ["Other", "Other machine"],
];
const normaliseVehicleType = (v) => {
  const raw = (v || "").trim();
  if (!raw) return "Ute";
  const hit = VEHICLE_TYPE_OPTIONS.find(([val]) => val.toLowerCase() === raw.toLowerCase());
  return hit ? hit[0] : "Other";
};

export default function Fleet({ trucksOnly = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isOffice = user.role !== "field";
  const [desktop, setDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const resize = () => setDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const openProfile = (event, id) => { if (!isFleetAction(event)) nav(fleetProfilePath(id)); };
  const [items, setItems] = useState([]);
  const [fleetForms, setFleetForms] = useState([]);
  const [damage, setDamage] = useState([]);
  const [users, setUsers] = useState([]);
  const [damageOpen, setDamageOpen] = useState(false);
  const [damageVehicle, setDamageVehicle] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [utilOpen, setUtilOpen] = useState(false);
  const [serviceVehicle, setServiceVehicle] = useState(null);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [view, setView] = useState(() => localStorage.getItem("fleet_view") || "list");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("fleet_view", v); };

  const load = async () => {
    const [v, d] = await Promise.all([api.get("/fleet"), api.get("/damage")]);
    setItems(v.data);
    setDamage(d.data);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/form_templates")
      .then((r) => setFleetForms((r.data || []).filter((t) => t.category === "fleet" && t.active)))
      .catch(() => {});
  }, []);

  const userById = (id) => users.find((u) => u.id === id);
  const userNameById = (id) => userById(id)?.name || null;

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/fleet/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fleet-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fleet report downloaded — opens straight into Excel");
    } catch { toast.error("Export failed"); }
  };

  const damageFor = (vid) => damage.filter((d) => d.vehicle_id === vid);

  const remove = async (id) => {
    if (!window.confirm("Retire this vehicle?")) return;
    await api.delete(`/fleet/${id}`);
    toast.success("Vehicle removed");
    load();
  };

  const markRepaired = async (id) => {
    await api.patch(`/damage/${id}?status=repaired`);
    toast.success("Marked repaired");
    load();
  };

  const reassign = async (v, uid) => {
    try {
      await api.patch(`/fleet/${v.id}`, {
        rego: v.rego, fleet_number: v.fleet_number, make: v.make, model: v.model, year: v.year, vehicle_type: v.vehicle_type,
        odometer: v.odometer, machine_hours: v.machine_hours, wof_expiry: v.wof_expiry, rego_expiry: v.rego_expiry,
        rego_on_hold: !!v.rego_on_hold,
        service_due: v.service_due, assigned_user_id: uid || null, status: v.status, notes: v.notes,
      });
      toast.success(uid ? `Assigned to ${userNameById(uid)}` : "Unassigned");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const q = search.trim().toLowerCase();
  const scopedItems = trucksOnly ? items.filter((v) => /truck|tipper|hiab/i.test(v.vehicle_type || "")) : items;
  const visible = q
    ? scopedItems.filter((v) => [v.fleet_number, v.rego, v.make, v.model, v.vehicle_type, v.status, userNameById(v.assigned_user_id)]
        .some((f) => (f || "").toLowerCase().includes(q)))
    : scopedItems;
  const groups = [
    { key: "machines", label: "Machines", items: visible.filter((v) => meterTypeFor(v.vehicle_type) === "hours") },
    { key: "vehicles", label: "Vehicles", items: visible.filter((v) => meterTypeFor(v.vehicle_type) === "km") },
    { key: "trailers", label: "Trailers & attachments", items: visible.filter((v) => meterTypeFor(v.vehicle_type) === "none") },
  ].filter((g) => g.items.length > 0);

  return (
    <div data-testid="fleet-page" className="p-6 lg:p-10 w-full">
      <PageHeader
        tag="/ Vehicles & plant"
        title={trucksOnly ? "Trucks" : "Plant & Fleet"}
        subtitle={trucksOnly ? "Company trucks from Plant & Fleet. Fleet remains the asset record; filter uses recorded vehicle type." : "Manage company equipment and vehicles."}
        action={isOffice && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCsv}
                    className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold"
                    data-testid="fleet-export-csv-button">
              <Download className="w-4 h-4 mr-2" /> {trucksOnly ? "Export all Fleet CSV" : "Export CSV"}
            </Button>
            <Button variant="outline" onClick={() => setUtilOpen(true)}
                    className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold"
                    data-testid="open-utilisation-button">
              <BarChart3 className="w-4 h-4 mr-2" /> {trucksOnly ? "All Fleet utilisation" : "Utilisation"}
            </Button>
            <Button onClick={() => nav("/app/fleet/new")}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                    data-testid="new-vehicle-button">
              <Plus className="w-4 h-4 mr-2" /> Add vehicle
            </Button>
          </div>
        )}
      />

      <FleetUtilisationDialog open={utilOpen} onClose={() => setUtilOpen(false)} />

      {fleetForms.length > 0 && (
        <div className="mb-5" data-testid="fleet-custom-forms">
          <div className="label-tag mb-2 flex items-center gap-1.5">
            <ClipboardList className="w-3 h-3" /> Fleet & plant forms
          </div>
          <div className="flex flex-wrap gap-2">
            {fleetForms.map((t) => (
              <button key={t.id} onClick={() => nav(`/app/forms?fill=${t.id}`)}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 bg-white px-3 py-2 rounded-sm hover:border-[#FF5F15] hover:text-[#FF5F15] transition-colors"
                data-testid={`fleet-form-${t.id}`}>
                <ClipboardList className="w-3.5 h-3.5" /> {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isOffice && <DefaultFleetRateStrip />}

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fleet # / rego / make / model / operator…"
              className="pl-9 rounded-sm border-zinc-400"
              data-testid="fleet-search-input"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                data-testid="fleet-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="hidden md:flex items-center rounded-lg border border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620] p-0.5 ml-auto" data-testid="fleet-view-toggle">
            <button
              onClick={() => setViewPersist("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "list" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
              data-testid="fleet-view-list">
              <LayoutList className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setViewPersist("cards")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "cards" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
              data-testid="fleet-view-cards">
              <LayoutGrid className="w-3.5 h-3.5" /> Cards
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No vehicles yet" description={isOffice ? "Add your first vehicle to start tracking." : "No fleet records visible."} />
      ) : visible.length === 0 ? (
        <EmptyState title={trucksOnly && !search ? "No truck-typed Fleet assets" : "No matches"} description={trucksOnly && !search ? "Truck, tipper and HIAB vehicle types appear here. Manage vehicle types in Plant & Fleet." : `Nothing in the fleet matches "${search}".`} />
      ) : view === "list" && desktop ? (
        <div className="space-y-6" data-testid="fleet-list">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center gap-2 mb-2">
                <span className="label-tag" data-testid={`fleet-group-${g.key}`}>{g.label}</span>
                <span className="text-[11px] font-mono text-zinc-400">{g.items.length}</span>
              </div>
              <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-hidden">
                {g.items.map((v) => (
                  <FleetListRow key={v.id} v={v} user={user} isOffice={isOffice} users={users}
                    assignedName={userNameById(v.assigned_user_id)} damage={damageFor(v.id)}
                    expanded={!!expanded[v.id]} onToggleExpand={() => setExpanded((s) => ({ ...s, [v.id]: !s[v.id] }))}
                    onOpen={(event) => openProfile(event, v.id)} onReassign={reassign} onEdit={() => nav(`/app/fleet/${v.id}/edit`)}
                    onRemove={() => remove(v.id)} onDamage={() => { setDamageVehicle(v); setDamageOpen(true); }}
                    onService={() => setServiceVehicle(v)} onMarkRepaired={markRepaired} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
          <div key={g.key}>
            <div className="flex items-center gap-2 mb-3">
              <span className="label-tag" data-testid={`fleet-group-${g.key}`}>{g.label}</span>
              <span className="text-[11px] font-mono text-zinc-400">{g.items.length}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {g.items.map((v) => {
            const assignedName = userNameById(v.assigned_user_id);
            const isMine = v.assigned_user_id === user.id;
            return (
            <div key={v.id} onClick={event => openProfile(event, v.id)} className={`hard-card p-4 cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-500 transition-colors ${isMine ? "border-[#FF5F15] border-2" : ""}`} data-testid={`vehicle-card-${v.id}`}>
              <Link to={fleetProfilePath(v.id)} aria-label={`Open ${[v.make, v.model].filter(Boolean).join(' ') || v.rego || 'asset'} profile`}
                className="flex w-full aspect-[16/9] mb-3 rounded-lg overflow-hidden items-center justify-center bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10"
                data-testid={`vehicle-photo-${v.id}`}>
                {v.photo_path ? <AuthImage path={v.photo_path} alt={`${v.make || ''} ${v.model || ''}`} className="w-full h-full object-cover" /> : <Truck className="w-12 h-12 text-zinc-400" />}
              </Link>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-zinc-400" />
                    {v.fleet_number && (
                      <span className="inline-flex items-center bg-zinc-900 text-white text-[11px] font-mono font-bold tracking-wider px-1.5 py-0.5 rounded-sm" data-testid={`fleet-number-${v.id}`}>
                        {v.fleet_number}
                      </span>
                    )}
                    <span className="font-mono text-sm font-bold tracking-wider">{v.rego}</span>
                    {isMine && <span className="inline-flex items-center gap-1 bg-[#FF5F15] text-white text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm">Your vehicle</span>}
                    {v.rego_on_hold && <span className="inline-flex items-center bg-zinc-200 text-zinc-700 text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm" data-testid={`rego-hold-badge-${v.id}`}>Rego on hold</span>}
                  </div>
                  <h3 className="font-semibold mt-1"><Link to={fleetProfilePath(v.id)} className="hover:text-[#FF5F15]">{[v.make, v.model].filter(Boolean).join(" ") || v.rego || "Asset"}</Link></h3>
                  {v.year && <div className="text-xs text-zinc-500">{v.year} · {v.vehicle_type || "Vehicle"}</div>}
                </div>
                <StatusBadge status={v.status} />
              </div>

              <div className="mt-3 flex items-center gap-2 text-xs" data-testid={`assigned-${v.id}`}>
                <User className="w-3.5 h-3.5 text-zinc-400" />
                {assignedName ? (
                  <span className="text-zinc-700"><span className="label-tag mr-1">Assigned:</span><span className="font-bold">{assignedName}</span></span>
                ) : v.assigned_user_id ? (
                  <span className="text-zinc-500 italic">Assigned (worker not visible)</span>
                ) : (
                  <span className="text-zinc-400 italic">Unassigned · in the yard</span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <DataPoint label={meterInfo(v).label} value={meterInfo(v).value} />
                <DataPoint label="WoF" value={v.wof_expiry || "—"} status={v.rego_on_hold ? null : expiryStatus(v.wof_expiry)} />
                <DataPoint label="Rego" value={v.rego_on_hold ? "On hold" : (v.rego_expiry || "—")} status={v.rego_on_hold ? null : expiryStatus(v.rego_expiry)} />
                <DataPoint label="Service" value={v.service_due || "—"} alert={isPastOrSoon(v.service_due)} />
              </div>
              {(v.service_interval_hours || v.service_interval_months) && (
                <RuntimeChip vehicleId={v.id} onOpen={() => setServiceVehicle(v)} />
              )}
              {v.notes && <p className="mt-3 text-xs text-zinc-500 line-clamp-2">{v.notes}</p>}
              {damageFor(v.id).length > 0 && (
                <div className="mt-3 border-t border-zinc-200 pt-3">
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => ({ ...s, [v.id]: !s[v.id] }))}
                    className="w-full flex items-center justify-between text-left hover:opacity-80"
                    data-testid={`damage-toggle-${v.id}`}
                  >
                    <span className="label-tag text-red-700">
                      Damage ({damageFor(v.id).filter((d) => d.status === "open").length} open / {damageFor(v.id).length} total)
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${expanded[v.id] ? "rotate-180" : ""}`} />
                  </button>
                  {expanded[v.id] ? (
                    <div className="mt-2 space-y-3">
                      {damageFor(v.id).map((d) => (
                        <div key={d.id} className="text-xs bg-zinc-50 border border-zinc-200 p-2.5" data-testid={`damage-row-${d.id}`}>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block px-1.5 py-0.5 text-[11px] font-bold uppercase rounded-sm ${d.severity === "major" ? "bg-red-600 text-white" : d.severity === "moderate" ? "bg-orange-500 text-white" : "bg-yellow-300 text-zinc-900"}`}>{d.severity}</span>
                              <span className={`inline-block px-1.5 py-0.5 text-[11px] font-bold uppercase rounded-sm border ${d.status === "open" ? "bg-red-50 text-red-700 border-red-300" : d.status === "repaired" ? "bg-green-50 text-green-700 border-green-300" : "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>{d.status}</span>
                              <span className="text-zinc-500">{d.user_name} · {new Date(d.created_at).toLocaleDateString("en-NZ")}</span>
                            </div>
                            {isOffice && d.status === "open" && (
                              <button onClick={() => markRepaired(d.id)} className="text-[#FF5F15] hover:underline font-bold uppercase text-[11px] tracking-wider" data-testid={`mark-repaired-${d.id}`}>
                                Mark repaired
                              </button>
                            )}
                          </div>
                          <p className="text-zinc-700 mt-1.5 whitespace-pre-wrap">{d.description}</p>
                          {d.odometer && <div className="text-[11px] text-zinc-500 font-mono mt-1">{meterInfo(v).type === "hours" ? `Meter: ${d.odometer.toLocaleString()} hrs` : `Odo: ${d.odometer.toLocaleString()} km`}</div>}
                          {d.photo_paths?.length > 0 && (
                            <div className="mt-2">
                              <PhotoGrid paths={d.photo_paths} onClick={(p) => setLightbox(p)} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 space-y-1.5">
                      {damageFor(v.id).slice(0, 2).map((d) => (
                        <div key={d.id} className="text-xs flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <span className={`inline-block px-1.5 py-0.5 mr-1.5 text-[11px] font-bold uppercase rounded-sm ${d.severity === "major" ? "bg-red-600 text-white" : d.severity === "moderate" ? "bg-orange-500 text-white" : "bg-yellow-300 text-zinc-900"}`}>{d.severity}</span>
                            <span className="text-zinc-700">{d.description}</span>
                          </div>
                          {isOffice && d.status === "open" && (
                            <button onClick={() => markRepaired(d.id)} className="text-[#FF5F15] hover:underline font-bold uppercase text-[11px] tracking-wider" data-testid={`mark-repaired-${d.id}`}>
                              Mark repaired
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-zinc-200 flex justify-between items-center gap-2 flex-wrap">
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline"
                    className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs"
                    onClick={() => { setDamageVehicle(v); setDamageOpen(true); }}
                    data-testid={`report-damage-${v.id}`}
                  >
                    <Wrench className="w-3.5 h-3.5 mr-1" /> Report damage
                  </Button>
                  {isOffice && (
                    <Button size="sm" variant="outline"
                      className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs"
                      onClick={() => setServiceVehicle(v)}
                      data-testid={`service-log-${v.id}`}
                    >
                      <Activity className="w-3.5 h-3.5 mr-1" /> Service log
                    </Button>
                  )}
                </div>
                {isOffice && (
                  <div className="flex items-center gap-1">
                    <Select value={v.assigned_user_id || "__none__"} onValueChange={(uid) => reassign(v, uid === "__none__" ? null : uid)}>
                      <SelectTrigger className="h-8 w-[160px] rounded-sm border-zinc-400 text-xs" data-testid={`assign-${v.id}`}>
                        <SelectValue placeholder="Assign…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__"><span className="italic text-zinc-500">Unassigned</span></SelectItem>
                        {users.filter((u) => u.role !== "super_admin").map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-zinc-400 text-[11px] ml-1">{u.role}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => nav(`/app/fleet/${v.id}/edit`)} data-testid={`edit-vehicle-${v.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-red-600" onClick={() => remove(v.id)} data-testid={`delete-vehicle-${v.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
            </div>
          </div>
          ))}
        </div>
      )}

      <Dialog open={damageOpen} onOpenChange={(v) => { setDamageOpen(v); if (!v) setDamageVehicle(null); }}>
        {damageVehicle && <DamageForm vehicle={damageVehicle} onDone={() => { setDamageOpen(false); setDamageVehicle(null); load(); }} />}
      </Dialog>
      {serviceVehicle && (
        <ServiceLogDialog
          vehicle={serviceVehicle}
          onClose={() => setServiceVehicle(null)}
          onChanged={load}
        />
      )}
      <PhotoLightbox path={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function ExpiryCell({ label, value, status, alert }) {
  const bad = (status && status.severity !== "ok" && status.severity !== "none") || alert;
  return (
    <div className="text-center min-w-[64px]">
      <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`text-[11px] font-mono font-bold flex items-center justify-center gap-0.5 ${bad ? "text-red-600" : "text-zinc-700 dark:text-zinc-300"}`}>
        {value || "—"}
        {bad && <AlertCircle className="w-3 h-3" />}
      </div>
    </div>
  );
}

export function FleetListRow({ v, user, isOffice, users, assignedName, damage, expanded, onToggleExpand,
                        onOpen, onReassign, onEdit, onRemove, onDamage, onService, onMarkRepaired }) {
  const isMine = v.assigned_user_id === user.id;
  const openDamage = damage.filter((d) => d.status === "open").length;
  return (
    <div className={`border-b border-zinc-100 dark:border-white/5 last:border-b-0 ${isMine ? "bg-orange-50/50 dark:bg-orange-900/5" : ""}`} data-testid={`fleet-row-${v.id}`}>
      <div onClick={onOpen} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/[0.04] transition-colors">
        <Link to={fleetProfilePath(v.id)} aria-label={`Open ${[v.make, v.model].filter(Boolean).join(' ') || v.rego || 'asset'} profile`}
          className="w-12 h-12 rounded-lg shrink-0 overflow-hidden border border-zinc-200 dark:border-white/10 flex items-center justify-center bg-zinc-100 dark:bg-white/5 text-zinc-400" data-testid={`vehicle-photo-${v.id}`}>
          {v.photo_path ? <AuthImage path={v.photo_path} alt={v.rego || 'Asset'} className="w-full h-full object-cover" /> : <Truck className="w-5 h-5" />}
        </Link>
        <div className="min-w-0 w-[170px] xl:w-[210px] shrink">
          <Link to={fleetProfilePath(v.id)} className="block font-semibold text-sm leading-snug mb-1 hover:text-[#FF5F15] truncate">{[v.make, v.model].filter(Boolean).join(" ") || v.rego || 'Asset'}</Link>
          <div className="flex items-center gap-1.5">
            {v.fleet_number && <span className="text-muted-foreground text-xs font-mono" data-testid={`fleet-number-${v.id}`}>{v.fleet_number}</span>}
            <span className="font-mono text-xs text-muted-foreground truncate">{v.rego}</span>
            {isMine && <span className="bg-[#FF5F15] text-white text-[11px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm shrink-0">Yours</span>}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">{v.vehicle_type || "Vehicle"}{v.year ? ` · ${v.year}` : ""}</div>
        </div>
        <div className="hidden lg:block w-[130px] shrink-0 text-xs truncate" data-testid={`assigned-${v.id}`}>
          {assignedName ? <span className="font-bold">{assignedName}</span> : <span className="text-zinc-400 italic">Unassigned</span>}
        </div>
        <div className="hidden xl:flex items-center gap-4 shrink-0">
          <ExpiryCell label={meterInfo(v).shortLabel} value={meterInfo(v).shortValue} />
          <ExpiryCell label="WoF" value={v.wof_expiry} status={v.rego_on_hold ? null : expiryStatus(v.wof_expiry)} />
          <ExpiryCell label="Rego" value={v.rego_on_hold ? "On hold" : v.rego_expiry} status={v.rego_on_hold ? null : expiryStatus(v.rego_expiry)} />
          <ExpiryCell label="Service" value={v.service_due} alert={isPastOrSoon(v.service_due)} />
        </div>
        <div className="flex-1" />
        {damage.length > 0 && (
          <button type="button" onClick={onToggleExpand}
            className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${openDamage ? "bg-red-50 text-red-700 border-red-300" : "bg-zinc-100 text-zinc-600 border-zinc-300"}`}
            data-testid={`damage-toggle-${v.id}`}>
            <Wrench className="w-3 h-3" /> {openDamage ? `${openDamage} open` : `${damage.length} fixed`}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
        <StatusBadge status={v.status} />
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" title="Report damage" onClick={onDamage} data-testid={`report-damage-${v.id}`}>
            <Wrench className="w-3.5 h-3.5" />
          </Button>
          {isOffice && (
            <>
              <Select value={v.assigned_user_id || "__none__"} onValueChange={(uid) => onReassign(v, uid === "__none__" ? null : uid)}>
                <SelectTrigger className="h-8 w-[42px] lg:w-[130px] rounded-sm border-zinc-400 text-xs" data-testid={`assign-${v.id}`}>
                  <span className="hidden lg:inline truncate"><SelectValue placeholder="Assign…" /></span>
                  <User className="w-3.5 h-3.5 lg:hidden" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span className="italic text-zinc-500">Unassigned</span></SelectItem>
                  {users.filter((u) => u.role !== "super_admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-zinc-400 text-[11px] ml-1">{u.role}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DropdownMenu><DropdownMenuTrigger asChild><Button size="icon" variant="ghost" aria-label={`Actions for ${v.rego || v.fleet_number || [v.make, v.model].filter(Boolean).join(" ") || "asset"}`}><MoreHorizontal className="w-4 h-4"/></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit} data-testid={`edit-vehicle-${v.id}`}>Edit Plant</DropdownMenuItem>
                  <DropdownMenuItem onSelect={onService} data-testid={`service-log-${v.id}`}>Service log</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onRemove} className="text-red-600" data-testid={`delete-vehicle-${v.id}`}>Delete Plant</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
      {expanded && damage.length > 0 && (
        <div className="px-4 pb-3 space-y-2">
          {damage.map((d) => (
            <div key={d.id} className="text-xs bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 p-2.5" data-testid={`damage-row-${d.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-1.5 py-0.5 text-[11px] font-bold uppercase rounded-sm ${d.severity === "major" ? "bg-red-600 text-white" : d.severity === "moderate" ? "bg-orange-500 text-white" : "bg-yellow-300 text-zinc-900"}`}>{d.severity}</span>
                  <span className={`inline-block px-1.5 py-0.5 text-[11px] font-bold uppercase rounded-sm border ${d.status === "open" ? "bg-red-50 text-red-700 border-red-300" : "bg-green-50 text-green-700 border-green-300"}`}>{d.status}</span>
                  <span className="text-zinc-500">{d.user_name} · {new Date(d.created_at).toLocaleDateString("en-NZ")}</span>
                </div>
                {isOffice && d.status === "open" && (
                  <button onClick={() => onMarkRepaired(d.id)} className="text-[#FF5F15] hover:underline font-bold uppercase text-[11px] tracking-wider" data-testid={`mark-repaired-${d.id}`}>
                    Mark repaired
                  </button>
                )}
              </div>
              <p className="text-zinc-700 dark:text-zinc-300 mt-1.5 whitespace-pre-wrap">{d.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DataPoint({ label, value, alert, status }) {
  // `status` (from expiryStatus) takes priority — gives us tiered severity colours.
  // Falls back to legacy boolean `alert` for fields not yet upgraded (e.g. Service).
  if (status && status.severity !== "ok" && status.severity !== "none") {
    return (
      <div className={`p-2 border ${status.cell}`} data-testid={`expiry-${status.severity}`}>
        <div className="label-tag text-[11px] flex items-center justify-between">
          <span>{label}</span>
          <span className="font-bold tracking-wider">{status.label}</span>
        </div>
        <div className="metric text-sm font-bold flex items-center gap-1 mt-0.5">
          {value}
          <AlertCircle className="w-3 h-3" />
        </div>
      </div>
    );
  }
  return (
    <div className={`p-2 ${alert ? "bg-red-50 border border-red-200" : "bg-zinc-50 dark:bg-white/5"}`}>
      <div className="label-tag text-[11px]">{label}</div>
      <div className={`metric text-sm font-bold flex items-center gap-1 ${alert ? "text-red-700" : ""}`}>
        {value}
        {alert && <AlertCircle className="w-3 h-3" />}
      </div>
    </div>
  );
}

export function VehicleForm({ initial, users = [], onDone, onCancel, variant = "dialog" }) {
  const [form, setForm] = useState(initial ? {
    rego: initial.rego || "",
    fleet_number: initial.fleet_number || "",
    make: initial.make || "",
    model: initial.model || "",
    year: initial.year || "",
    vehicle_type: normaliseVehicleType(initial.vehicle_type),
    odometer: initial.odometer || 0,
    machine_hours: initial.machine_hours ?? "",
    wof_expiry: initial.wof_expiry || "",
    rego_expiry: initial.rego_expiry || "",
    rego_on_hold: !!initial.rego_on_hold,
    service_due: initial.service_due || "",
    status: initial.status || "active",
    notes: initial.notes || "",
    assigned_user_id: initial.assigned_user_id || "",
    photo_path: initial.photo_path || "",
    internal_hourly_rate: initial.internal_hourly_rate ?? "",
    charge_out_rate: initial.charge_out_rate ?? "",
    starting_hours: initial.starting_hours ?? "",
    service_interval_hours: initial.service_interval_hours ?? "",
    service_interval_months: initial.service_interval_months ?? "",
  } : {
    rego: "", fleet_number: "", make: "", model: "", year: "", vehicle_type: "Ute",
    odometer: 0, machine_hours: "", wof_expiry: "", rego_expiry: "", service_due: "",
    rego_on_hold: false,
    status: "active", notes: "", assigned_user_id: "", photo_path: "",
    internal_hourly_rate: "",
    charge_out_rate: "",
    starting_hours: "", service_interval_hours: "", service_interval_months: "",
  });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const formMeter = meterTypeFor(form.vehicle_type);

  const onPhotoChange = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "fleet");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_path: data.storage_path }));
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        odometer: Number(form.odometer) || 0,
        machine_hours: form.machine_hours === "" || form.machine_hours == null ? null : Number(form.machine_hours),
        assigned_user_id: form.assigned_user_id || null,
        internal_hourly_rate: form.internal_hourly_rate === "" || form.internal_hourly_rate == null
          ? null
          : Number(form.internal_hourly_rate),
        charge_out_rate: form.charge_out_rate === "" || form.charge_out_rate == null ? null : Number(form.charge_out_rate),
        starting_hours: form.starting_hours === "" || form.starting_hours == null ? null : Number(form.starting_hours),
        service_interval_hours: form.service_interval_hours === "" || form.service_interval_hours == null ? null : Number(form.service_interval_hours),
        service_interval_months: form.service_interval_months === "" || form.service_interval_months == null ? null : Number(form.service_interval_months),
      };
      if (initial) {
        await api.patch(`/fleet/${initial.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/fleet", payload);
        toast.success("Vehicle added");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const isPage = variant === "page";
  const Shell = isPage ? PageShell : DialogShell;
  return (
    <Shell initial={initial}>
      <form onSubmit={submit}
        className={isPage
          ? "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-10"
          : "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-8"}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Fleet #</Label>
            <Input value={form.fleet_number} onChange={set("fleet_number")} placeholder="Auto e.g. F012"
              className="mt-2 rounded-sm border-zinc-400 uppercase" data-testid="vehicle-fleet-number-input" />
          </div>
          <div>
            <Label className="label-tag">Rego</Label>
            <Input required value={form.rego} onChange={set("rego")} className="mt-2 rounded-sm border-zinc-400 uppercase" data-testid="vehicle-rego-input" />
          </div>
        </div>
        <div>
          <Label className="label-tag">Type</Label>
          <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPE_OPTIONS.map(([val, label]) => (
                <SelectItem key={val} value={val}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label className="label-tag">Make</Label>
            <Input required value={form.make} onChange={set("make")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-make-input" />
          </div>
          <div>
            <Label className="label-tag">Year</Label>
            <Input type="number" value={form.year} onChange={set("year")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-year-input" />
          </div>
        </div>
        <div>
          <Label className="label-tag">Model</Label>
          <Input required value={form.model} onChange={set("model")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-model-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {formMeter === "hours" ? (
            <div>
              <Label className="label-tag">Machine hours</Label>
              <Input type="number" step="0.1" value={form.machine_hours} onChange={set("machine_hours")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-machine-hours-input" />
            </div>
          ) : formMeter === "km" ? (
            <div>
              <Label className="label-tag">Odometer (km)</Label>
              <Input type="number" value={form.odometer} onChange={set("odometer")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-odo-input" />
            </div>
          ) : (
            <div>
              <Label className="label-tag">Meter</Label>
              <div className="mt-2 h-10 flex items-center px-3 border border-dashed border-zinc-300 rounded-sm text-xs text-zinc-400" data-testid="vehicle-no-meter">No meter for trailers</div>
            </div>
          )}
          <div>
            <Label className="label-tag">Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-tag">Assigned to</Label>
          <Select value={form.assigned_user_id || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === "__none__" ? "" : v })}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-assignee-select">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__"><span className="italic text-zinc-500">Unassigned · in the yard</span></SelectItem>
              {users.filter((u) => u.role !== "super_admin").map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-zinc-400 text-[11px] ml-1">{u.role}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 grid grid-cols-3 gap-3">
          <div>
            <Label className="label-tag">WoF expiry</Label>
            <Input type="date" value={form.wof_expiry} onChange={set("wof_expiry")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-wof-input" />
          </div>
          <div>
            <Label className="label-tag">Rego expiry</Label>
            <Input type="date" value={form.rego_expiry} onChange={set("rego_expiry")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-regoexp-input" />
          </div>
          <div>
            <Label className="label-tag">Service due</Label>
            <Input type="date" value={form.service_due} onChange={set("service_due")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-service-input" />
          </div>
        </div>
        <label className="md:col-span-2 flex items-start gap-2.5 cursor-pointer select-none border border-zinc-300 rounded-sm p-3 hover:border-[#FF5F15] transition-colors">
          <input
            type="checkbox"
            checked={!!form.rego_on_hold}
            onChange={(e) => setForm((f) => ({ ...f, rego_on_hold: e.target.checked }))}
            className="w-4 h-4 accent-[#FF5F15] mt-0.5"
            data-testid="vehicle-rego-hold-input"
          />
          <span className="text-sm">
            <span className="font-bold">Rego on hold</span>
            <span className="block text-xs text-zinc-500 mt-0.5">Vehicle's off the road — WoF and rego reminders won't show on the overview until you untick this.</span>
          </span>
        </label>
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Internal running rate <span className="text-zinc-400 normal-case">— NZD/hr, job costing</span></Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.internal_hourly_rate}
                onChange={set("internal_hourly_rate")}
                placeholder="e.g. 85"
                className="rounded-sm border-zinc-400 pl-7 pr-12"
                data-testid="vehicle-rate-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">/ hr</span>
            </div>
          </div>
          <div>
            <Label className="label-tag">Charge-out rate <span className="text-zinc-400 normal-case">— NZD/hr on invoices</span></Label>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.charge_out_rate}
                onChange={set("charge_out_rate")}
                placeholder="e.g. 150"
                className="rounded-sm border-zinc-400 pl-7 pr-12"
                data-testid="vehicle-charge-rate-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">/ hr</span>
            </div>
          </div>
          <div className="col-span-2 -mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            Internal rate falls back to the company default. Charge-out auto-fills the $/hr when importing this machine's hours onto an invoice.
          </div>
        </div>

        {/* Plant runtime + service tracking */}
        <div className="md:col-span-2 border-t-2 border-zinc-200 pt-4">
          <div className="label-tag mb-2 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Plant runtime &amp; servicing</div>
          <p className="text-[11px] text-zinc-500 mb-3 normal-case">Only fill these in for machines with an hour meter (excavators, loaders, compactors, etc.). Leave blank for utes and trailers.</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-tag">Starting hours <span className="text-zinc-400 normal-case">— when added</span></Label>
              <Input type="number" step="0.1" min="0" value={form.starting_hours} onChange={set("starting_hours")} placeholder="e.g. 4200" className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-starting-hours-input" />
            </div>
            <div>
              <Label className="label-tag">Service interval (h)</Label>
              <Input type="number" step="1" min="0" value={form.service_interval_hours} onChange={set("service_interval_hours")} placeholder="e.g. 250" className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-service-interval-hours-input" />
            </div>
            <div>
              <Label className="label-tag">Service interval (mo)</Label>
              <Input type="number" step="1" min="0" value={form.service_interval_months} onChange={set("service_interval_months")} placeholder="e.g. 6" className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-service-interval-months-input" />
            </div>
          </div>
          <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            Service due triggers when either threshold is reached — whichever comes first.
          </div>
        </div>
        <div>
          <Label className="label-tag">Vehicle photo <span className="text-zinc-400 normal-case">— optional</span></Label>
          {form.photo_path ? (
            <div className="mt-2 relative inline-block border border-zinc-300 bg-zinc-50">
              <AuthImage path={form.photo_path} alt="vehicle" className="h-32 w-auto object-cover" />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, photo_path: "" }))}
                className="absolute top-1 right-1 bg-zinc-900/80 text-white rounded-full p-1 hover:bg-red-600"
                data-testid="vehicle-photo-remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex items-center justify-center gap-2 h-20 border-2 border-dashed border-zinc-300 hover:border-[#FF5F15] cursor-pointer text-sm text-zinc-500 hover:text-[#FF5F15] transition-colors" data-testid="vehicle-photo-upload">
              {photoBusy ? <span className="font-mono">Uploading…</span> : <><Camera className="w-4 h-4" /> Tap to add photo</>}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoChange} disabled={photoBusy} />
            </label>
          )}
        </div>
        <div>
          <Label className="label-tag">Notes</Label>
          <Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-2 rounded-sm border-zinc-400" data-testid="vehicle-notes-input" />
        </div>
        {isPage ? (
          <div className="md:col-span-2 sticky bottom-0 z-20 mt-2 border-t border-zinc-200 dark:border-white/10 bg-white/95 dark:bg-[#0B0F14]/95 backdrop-blur py-3 pr-20 sm:pr-24 flex items-center justify-end gap-3">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}
                className="rounded-sm border-zinc-400 uppercase tracking-wider font-bold" data-testid="vehicle-cancel-button">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="vehicle-submit-button">
              {busy ? "Saving..." : initial ? "Save changes" : "Add vehicle"}
            </Button>
          </div>
        ) : (
          <DialogFooter className="md:col-span-2">
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="vehicle-submit-button">
              {busy ? "Saving..." : initial ? "Save changes" : "Add vehicle"}
            </Button>
          </DialogFooter>
        )}
      </form>
    </Shell>
  );
}

const VEHICLE_FORM_BLURB = 'Track WoF, rego and service expiries. Expiries within 30 days are flagged. Assign an operator so they see "Your vehicle" highlighted.';

function DialogShell({ initial, children }) {
  return (
    <DialogContent className="rounded-sm max-w-lg md:max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">{initial ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
        <DialogDescription>{VEHICLE_FORM_BLURB}</DialogDescription>
      </DialogHeader>
      {children}
    </DialogContent>
  );
}

function PageShell({ children }) {
  return <div className="max-w-5xl min-w-0 overflow-x-hidden">{children}</div>;
}

export function DamageForm({ vehicle, onDone }) {
  const dmgMeter = meterTypeFor(vehicle.vehicle_type);
  const [form, setForm] = useState({ severity: "minor", description: "", odometer: (dmgMeter === "hours" ? vehicle.machine_hours : vehicle.odometer) || "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onPhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const out = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "damage");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data.storage_path);
      }
      setPhotos((p) => [...p, ...out]);
      toast.success(`Uploaded ${out.length} photo(s)`);
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/damage", {
        vehicle_id: vehicle.id,
        severity: form.severity,
        description: form.description,
        odometer: form.odometer ? Number(form.odometer) : null,
        photo_paths: photos,
      });
      toast.success("Damage report submitted");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm max-w-md">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">Report damage</DialogTitle>
        <DialogDescription>{vehicle.rego} · {vehicle.make} {vehicle.model}. Office will be notified.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Severity</Label>
          <Select value={form.severity} onValueChange={set("severity")}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="damage-severity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="minor">Minor (scratch, dent)</SelectItem>
              <SelectItem value="moderate">Moderate (panel, light)</SelectItem>
              <SelectItem value="major">Major (drivable but unsafe / not drivable)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">What happened?</Label>
          <Textarea required rows={3} value={form.description} onChange={set("description")} className="mt-2 rounded-sm border-zinc-400" placeholder="Reversed into bollard at depot, dented rear bumper..." data-testid="damage-desc-input" />
        </div>
        {dmgMeter !== "none" && (
        <div>
          <Label className="label-tag">{dmgMeter === "hours" ? "Machine hours" : "Odometer (km)"}</Label>
          <Input type="number" value={form.odometer} onChange={set("odometer")} className="mt-2 rounded-sm border-zinc-400" data-testid="damage-odo-input" />
        </div>
        )}
        <div>
          <Label className="label-tag">Photos ({photos.length})</Label>
          <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-4 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]" data-testid="damage-photo-upload">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-bold uppercase tracking-wider">{uploading ? "Uploading..." : "Add photos"}</span>
            <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPhotoChange} disabled={uploading} />
          </label>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="damage-submit-button">
            {busy ? "Submitting..." : "Submit report"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


// ============================================================
//  FLEET UTILISATION DIALOG
// ============================================================
const DOW_SHORT = ["S", "M", "T", "W", "T", "F", "S"];
const VEHICLE_TYPES = new Set(["ute", "truck", "van", "car", "trailer", "pickup", "vehicle"]);

function shiftIso(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return localIsoDay(d);
}
function todayIso() { return localIsoDay(new Date()); }
function dowOf(iso) { return new Date(iso + "T00:00:00").getDay(); }
function prettyShort(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}

function FleetUtilisationDialog({ open, onClose }) {
  const [end, setEnd] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const start = shiftIso(end, -6);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fleet/utilisation", { params: { start, end } });
      setData(data);
    } catch (e) {
      toast.error("Failed to load utilisation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, end]);

  if (!open) return null;

  // Find max hours in any single day for chart scaling
  const maxDayHours = data?.assets?.reduce((m, a) => {
    const local = Math.max(...Object.values(a.by_day || {}), 0);
    return Math.max(m, local);
  }, 0) || 0;
  const barScale = Math.max(maxDayHours, 8); // floor of 8h so a standard day fills bar

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="fleet-utilisation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Fleet utilisation
          </DialogTitle>
          <DialogDescription>
            Hours each asset ran over a 7-day window. Pulled live from approved &amp; pending timesheets.
          </DialogDescription>
        </DialogHeader>

        {/* Week selector */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4 pt-2">
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="rounded-sm"
                    onClick={() => setEnd(shiftIso(end, -7))}
                    data-testid="util-prev-week">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-3 py-1.5 bg-zinc-50 border border-zinc-300 min-w-[220px] text-center">
              <div className="label-tag">Window</div>
              <div className="font-mono text-xs font-bold" data-testid="util-window-label">
                {prettyShort(start)} → {prettyShort(end)}
              </div>
            </div>
            <Button size="icon" variant="outline" className="rounded-sm"
                    onClick={() => setEnd(shiftIso(end, 7))}
                    disabled={end >= todayIso()}
                    data-testid="util-next-week">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline"
                    className="rounded-sm ml-1 uppercase tracking-wider font-bold text-xs"
                    onClick={() => setEnd(todayIso())}
                    data-testid="util-this-week">This week</Button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 font-mono">Crunching numbers…</div>
        ) : data ? (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="util-totals">
              <UtilTile label="Fleet size" value={data.totals.fleet_size} accent="bg-[#09090B]" />
              <UtilTile label="Assets used" value={data.totals.assets_used} accent="bg-[#FF5F15]" />
              <UtilTile label="Idle assets" value={data.totals.assets_idle} accent="bg-red-600" emphasise={data.totals.assets_idle > 0} />
              <UtilTile label="Hours total" value={data.totals.total_hours.toFixed(1)} suffix="hrs" accent="bg-green-600" />
            </div>

            {data.assets.length === 0 ? (
              <EmptyState title="No fleet to analyse" description="Add some vehicles or machines and they'll show here." />
            ) : (
              <div className="space-y-3" data-testid="util-asset-list">
                {data.assets.map((a) => (
                  <UtilRow key={a.id} asset={a} days={data.days} scale={barScale} />
                ))}
              </div>
            )}
          </>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  className="rounded-sm uppercase tracking-wider font-bold"
                  data-testid="util-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UtilTile({ label, value, suffix, accent, emphasise }) {
  return (
    <div className={`relative bg-white border-2 p-3 ${emphasise ? "border-red-600" : "border-zinc-900"}`}>
      <div className={`absolute top-0 left-0 h-1 w-12 ${accent}`} />
      <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-2 flex items-end gap-1.5">
        <span className={`font-display font-black text-2xl leading-none ${emphasise ? "text-red-600" : ""}`}>{value}</span>
        {suffix && <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

function UtilRow({ asset, days, scale }) {
  const Icon = VEHICLE_TYPES.has((asset.vehicle_type || "").toLowerCase()) ? Truck : Wrench;
  const isIdle = asset.total_hours === 0;
  const isRetired = asset.status === "retired";

  return (
    <div
      className={`border-2 bg-white p-3 ${isIdle && !isRetired ? "border-red-500" : "border-zinc-900"}`}
      data-testid={`util-asset-${asset.id}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Asset identity */}
        <div className="flex items-center gap-2 min-w-[180px] flex-1">
          {asset.photo_path ? (
            <AuthImage path={asset.photo_path} alt={asset.rego} className="w-10 h-10 object-cover shrink-0" />
          ) : (
            <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${isIdle ? "bg-red-100 text-red-600" : "bg-[#09090B] text-[#FF5F15]"}`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-sm leading-tight flex items-center gap-1.5">
              <span className="font-mono tracking-wider">{asset.rego}</span>
              {isRetired && <span className="text-[11px] font-bold uppercase tracking-wider bg-zinc-200 text-zinc-700 px-1.5 py-0.5">Retired</span>}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest leading-tight truncate">
              {asset.make} {asset.model}
            </div>
          </div>
        </div>

        {/* Daily bar chart */}
        <div className="flex items-end gap-1 h-12">
          {days.map((d) => {
            const hrs = asset.by_day?.[d] || 0;
            const pct = scale > 0 ? Math.round((hrs / scale) * 100) : 0;
            const today = d === todayIso();
            return (
              <div key={d} className="flex flex-col items-center w-7" title={`${d}: ${hrs.toFixed(2)}h`}>
                <div className="relative h-9 w-full flex items-end">
                  <div
                    className={`w-full ${hrs > 0 ? "bg-[#FF5F15]" : "bg-zinc-100 border-t border-dashed border-zinc-300"}`}
                    style={{ height: hrs > 0 ? `${Math.max(8, pct)}%` : "4px" }}
                    data-testid={`util-bar-${asset.id}-${d}`}
                  />
                </div>
                <div className={`text-[11px] font-bold uppercase tracking-widest mt-0.5 ${today ? "text-[#FF5F15]" : "text-zinc-500"}`}>
                  {DOW_SHORT[dowOf(d)]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals on right */}
        <div className="text-right shrink-0 min-w-[110px]">
          <div className="flex items-end gap-1 justify-end">
            <span className={`font-display font-black text-2xl leading-none ${isIdle ? "text-red-600" : ""}`}>
              {asset.total_hours.toFixed(1)}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">hrs</span>
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
            {asset.active_days}d active · {asset.idle_days}d idle
          </div>
          {asset.jobs.length > 0 && (
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
              {asset.jobs.length} job{asset.jobs.length === 1 ? "" : "s"}
            </div>
          )}
          {asset.last_used_date && (
            <div className="text-[11px] font-mono text-zinc-400 mt-0.5">last: {asset.last_used_date}</div>
          )}
        </div>
      </div>

      {/* Idle banner */}
      {isIdle && !isRetired && (
        <div className="mt-2 text-[11px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-2 py-1 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> Sat in the yard all week · consider hiring out or selling
        </div>
      )}

      {/* Job breakdown */}
      {asset.jobs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {asset.jobs.map((j, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-zinc-100 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5">
              {j.title && j.title !== "—" ? j.title : <span className="text-zinc-400 italic normal-case font-normal">No job</span>}
              <span className="text-[#FF5F15] font-mono">{j.hours.toFixed(1)}h</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Default fleet $/hr — company-wide fallback used when an asset
// has no `internal_hourly_rate` and a job has no per-asset override.
// Shown only to office/admin; persists on the org doc via PATCH /org/me.
// ============================================================
function DefaultFleetRateStrip() {
  const [rate, setRate] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/org/me").then((r) => {
      const v = r.data?.default_fleet_hourly_rate;
      setRate(v == null ? "" : String(v));
      setSaved(v ?? null);
    }).finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const num = rate === "" ? null : Number(rate);
      await api.patch("/org/me", { default_fleet_hourly_rate: num });
      setSaved(num);
      toast.success(num == null ? "Default cleared" : `Default rate set to $${num}/hr`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;
  const dirty = String(saved ?? "") !== rate;

  return (
    <div className="mb-5 border-2 border-zinc-200 bg-white px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="default-fleet-rate-strip">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-700">Default fleet $/hr</span>
        <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">company-wide fallback</span>
      </div>
      <div className="relative max-w-[140px]">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder="e.g. 75"
          className="rounded-sm border-zinc-400 pl-7 pr-10 h-8 text-sm"
          data-testid="default-fleet-rate-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">/ hr</span>
      </div>
      {dirty && (
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
          data-testid="default-fleet-rate-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      )}
    </div>
  );
}



// --- Plant runtime chip on each fleet card -----------------------------------
function RuntimeChip({ vehicleId, onOpen }) {
  const [r, setR] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/fleet/${vehicleId}/runtime`).then(({ data }) => { if (alive) setR(data); }).catch(() => {});
    return () => { alive = false; };
  }, [vehicleId]);
  if (!r) return null;
  const cls = r.status === "overdue"
    ? "bg-red-100 border-red-500 text-red-900"
    : r.status === "due_soon"
    ? "bg-amber-100 border-amber-500 text-amber-900"
    : "bg-emerald-50 border-emerald-300 text-emerald-900";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`mt-3 w-full border ${cls} px-3 py-2 text-left hover:opacity-80 transition`}
      data-testid={`runtime-chip-${vehicleId}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" />
          <span className="text-[11px] font-bold uppercase tracking-widest">
            {r.current_meter.toFixed(0)}h on the clock
          </span>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest">
          {r.status === "overdue" && "Service overdue"}
          {r.status === "due_soon" && "Service due soon"}
          {r.status === "ok" && (r.due_in_hours != null ? `${Math.round(r.due_in_hours)}h to service` : "On schedule")}
          {r.status === "untracked" && "No interval set"}
        </span>
      </div>
      {r.reason && (
        <div className="text-[11px] mt-0.5 opacity-80 normal-case font-normal">{r.reason}</div>
      )}
    </button>
  );
}

// --- Service log dialog ------------------------------------------------------
export function ServiceLogDialog({ vehicle, onClose, onChanged }) {
  const [runtime, setRuntime] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/fleet/${vehicle.id}/runtime`),
        api.get(`/fleet/${vehicle.id}/services`),
      ]);
      setRuntime(r.data);
      setServices(s.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load service log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicle.id]);

  const removeService = async (sid) => {
    if (!window.confirm("Delete this service record?")) return;
    try {
      await api.delete(`/fleet/services/${sid}`);
      toast.success("Removed");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const maxMonthly = Math.max(1, ...(runtime?.monthly || []).map((m) => m.hours));
  const statusCls = runtime?.status === "overdue"
    ? "bg-red-600 text-white"
    : runtime?.status === "due_soon"
    ? "bg-amber-500 text-zinc-900"
    : runtime?.status === "ok"
    ? "bg-emerald-600 text-white"
    : "bg-zinc-200 text-zinc-700";

  const viewUrl = (path) => {
    const tok = localStorage.getItem("fl_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/view?path=${encodeURIComponent(path)}&auth=${tok || ""}`;
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">
            {vehicle.make} {vehicle.model} · <span className="font-mono">{vehicle.rego}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">Plant runtime, service history, and workshop invoices.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-xs text-zinc-500">Loading…</div>
        ) : (
          <>
            {/* Header tiles */}
            {runtime && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="border-2 border-zinc-200 p-3">
                  <div className="label-tag">Total on the clock</div>
                  <div className="font-mono font-black text-2xl mt-1">{runtime.current_meter.toFixed(0)}<span className="text-sm font-normal text-zinc-500">h</span></div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{runtime.starting_hours > 0 && `Started at ${runtime.starting_hours.toFixed(0)}h`}</div>
                </div>
                <div className="border-2 border-zinc-200 p-3">
                  <div className="label-tag">Since last service</div>
                  <div className="font-mono font-black text-2xl mt-1">{runtime.hours_since_service.toFixed(0)}<span className="text-sm font-normal text-zinc-500">h</span></div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{runtime.interval_hours ? `of ${runtime.interval_hours}h interval` : "no interval set"}</div>
                </div>
                <div className="border-2 border-zinc-200 p-3">
                  <div className="label-tag">Next service</div>
                  <div className="font-mono font-black text-lg mt-1">
                    {runtime.due_in_hours != null ? `${Math.round(runtime.due_in_hours)}h` : "—"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{runtime.due_on_date && `or by ${runtime.due_on_date}`}</div>
                </div>
                <div className={`p-3 ${statusCls}`} data-testid="service-status-tile">
                  <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">Status</div>
                  <div className="font-display font-black text-lg uppercase mt-1 tracking-tight">
                    {runtime.status === "overdue" && "Overdue"}
                    {runtime.status === "due_soon" && "Due soon"}
                    {runtime.status === "ok" && "On schedule"}
                    {runtime.status === "untracked" && "Untracked"}
                  </div>
                  {runtime.reason && <div className="text-[11px] mt-0.5 normal-case opacity-90">{runtime.reason}</div>}
                </div>
              </div>
            )}

            {/* Monthly runtime chart */}
            {runtime && runtime.monthly.length > 0 && (
              <div className="mt-4 border-2 border-zinc-200 p-3">
                <div className="label-tag mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Monthly runtime (last 12 months)</div>
                <div className="flex items-end gap-1 h-32">
                  {runtime.monthly.map((m) => (
                    <div key={m.month} className="flex-1 flex flex-col items-center" title={`${m.month}: ${m.hours}h`}>
                      <div className="text-[11px] font-mono font-bold text-zinc-700">{m.hours.toFixed(0)}</div>
                      <div className="w-full bg-[#FF5F15]" style={{ height: `${(m.hours / maxMonthly) * 100}%`, minHeight: "2px" }} />
                      <div className="text-[11px] text-zinc-500 mt-1 font-mono">{m.month.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service history */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tag flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Service history</div>
                <Button onClick={() => setAdding(true)} size="sm" className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="log-service-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Log service
                </Button>
              </div>
              {services.length === 0 ? (
                <div className="border-2 border-dashed border-zinc-300 p-6 text-center text-xs text-zinc-500" data-testid="services-empty">
                  No services logged yet. Click <strong>Log service</strong> after each workshop visit to keep the clock running accurately.
                </div>
              ) : (
                <div className="border border-zinc-300 divide-y divide-zinc-200">
                  {services.map((s) => (
                    <div key={s.id} className="px-3 py-2.5 flex items-start gap-3 hover:bg-zinc-50" data-testid={`service-row-${s.id}`}>
                      <Wrench className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm">{s.service_type}</span>
                          <span className="text-[11px] font-mono text-zinc-500">{s.serviced_date}</span>
                          {s.hours_at_service != null && (
                            <span className="text-[11px] font-mono bg-zinc-100 px-1.5 py-0.5">@ {s.hours_at_service.toFixed(0)}h</span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                          {s.vendor && <span>{s.vendor}</span>}
                          {s.cost != null && <span className="font-mono font-bold text-zinc-900">${s.cost.toFixed(2)}</span>}
                        </div>
                        {s.notes && <div className="text-[11px] text-zinc-600 italic mt-1">{s.notes}</div>}
                      </div>
                      {s.file_path && (
                        <a href={viewUrl(s.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center px-2 h-7 border border-zinc-300 hover:border-zinc-500 text-[11px] font-bold uppercase tracking-wider rounded-sm" data-testid={`service-invoice-${s.id}`}>
                          <ExternalLink className="w-3 h-3 mr-1" /> Invoice
                        </a>
                      )}
                      <button onClick={() => removeService(s.id)} className="text-red-700 hover:text-red-900 p-1" data-testid={`service-del-${s.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Close</Button>
        </DialogFooter>

        {adding && (
          <LogServiceForm
            vehicleId={vehicle.id}
            suggestedHours={runtime?.current_meter}
            onClose={() => setAdding(false)}
            onSaved={async () => { setAdding(false); await load(); onChanged?.(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function LogServiceForm({ vehicleId, suggestedHours, onClose, onSaved }) {
  const [form, setForm] = useState({
    service_type: "",
    serviced_date: new Date().toISOString().slice(0, 10),
    hours_at_service: suggestedHours != null ? String(Math.round(suggestedHours)) : "",
    cost: "",
    vendor: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.service_type.trim()) { toast.error("Service type required"); return; }
    if (!form.serviced_date) { toast.error("Date required"); return; }
    setBusy(true);
    try {
      let filePayload = {};
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "service_invoice");
        const { data: f } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        filePayload = {
          file_path: f.storage_path,
          file_name: f.original_filename,
          content_type: f.content_type,
        };
      }
      await api.post(`/fleet/${vehicleId}/services`, {
        service_type: form.service_type.trim(),
        serviced_date: form.serviced_date,
        hours_at_service: form.hours_at_service === "" ? null : Number(form.hours_at_service),
        cost: form.cost === "" ? null : Number(form.cost),
        vendor: form.vendor || null,
        notes: form.notes || null,
        ...filePayload,
      });
      toast.success("Service logged");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="log-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">Log a service</DialogTitle>
          <DialogDescription className="text-xs">Capture what was done so the next-service clock resets correctly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="label-tag">Service type *</Label>
            <Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="e.g. 250h scheduled · Oil + filter · Major" className="mt-1.5 rounded-sm border-zinc-400" data-testid="svc-type-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Date *</Label>
              <Input type="date" value={form.serviced_date} onChange={(e) => setForm({ ...form, serviced_date: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400" data-testid="svc-date-input" />
            </div>
            <div>
              <Label className="label-tag">Hour meter reading</Label>
              <Input type="number" step="0.1" value={form.hours_at_service} onChange={(e) => setForm({ ...form, hours_at_service: e.target.value })} placeholder="e.g. 4380" className="mt-1.5 rounded-sm border-zinc-400" data-testid="svc-hours-input" />
            </div>
            <div>
              <Label className="label-tag">Cost (NZD)</Label>
              <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="e.g. 850" className="mt-1.5 rounded-sm border-zinc-400" data-testid="svc-cost-input" />
            </div>
            <div>
              <Label className="label-tag">Vendor</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Cable Price, Gough CAT" className="mt-1.5 rounded-sm border-zinc-400" data-testid="svc-vendor-input" />
            </div>
          </div>
          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What was done?" className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid="svc-notes-input" />
          </div>
          <div>
            <Label className="label-tag">Invoice / photo <span className="text-zinc-400 normal-case">— optional</span></Label>
            <div className="mt-1.5">
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-2 file:border-zinc-300 file:bg-white file:text-xs file:font-bold file:uppercase file:tracking-wider hover:file:border-[#FF5F15] cursor-pointer"
                data-testid="svc-file-input"
              />
              {file && <div className="text-[11px] text-zinc-600 mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="svc-save">
            {busy ? "Saving…" : "Save service"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
